"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get IntegrationLog () {
        return IntegrationLog;
    },
    get IntegrationLogCsvProperties () {
        return IntegrationLogCsvProperties;
    },
    get IntegrationLogProperties () {
        return IntegrationLogProperties;
    },
    get IntegrationLogPublicApiProperties () {
        return IntegrationLogPublicApiProperties;
    },
    get IntegrationLogSftpProperties () {
        return IntegrationLogSftpProperties;
    },
    get IntegrationLogUserUpdates () {
        return IntegrationLogUserUpdates;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const _integration = require("./integration");
const IntegrationLogUserUpdates = _zod.z.object({
    userID: _zod.z.instanceof(_bson.ObjectId),
    insertedCount: _zod.z.number(),
    uniqueFieldUpdateCount: _zod.z.number(),
    updatedFields: _zod.z.object({
        updatedFields: _zod.z.array(_zod.z.record(_zod.z.string(), _zod.z.any()))
    }),
    hasError: _zod.z.boolean(),
    error: _zod.z.object({
        code: _zod.z.string(),
        message: _zod.z.string()
    }).or(_zod.z.array(_zod.z.object({
        code: _zod.z.string().optional(),
        message: _zod.z.string(),
        /** Set on per-field validation errors; absent on row-level errors. */ fieldName: _zod.z.string().optional()
    }))).or(_zod.z.null()),
    updatesFound: _zod.z.boolean(),
    userNotFound: _zod.z.boolean().optional(),
    created: _zod.z.instanceof(_bson.ObjectId).or(_zod.z.string())
});
const IntegrationLogSftpProperties = _zod.z.object({
    source_key: _zod.z.string(),
    source_bucket: _zod.z.string(),
    archive_key: _zod.z.string(),
    archive_bucket: _zod.z.string(),
    sftp_integration_config_id: _zod.z.instanceof(_bson.ObjectId),
    testing_mode: _zod.z.boolean()
});
const IntegrationLogCsvProperties = _zod.z.object({
    field_mapping_object: _zod.z.record(_zod.z.string(), _zod.z.unknown()),
    field_map_used: _zod.z.record(_zod.z.string(), _zod.z.instanceof(_bson.ObjectId)),
    csv_hash: _zod.z.string().optional(),
    num_users_in_csv: _zod.z.number().optional(),
    num_fields_in_csv: _zod.z.number().optional(),
    csv_fields_to_skip: _zod.z.array(_zod.z.string()).optional(),
    /** Whether the file arrived PGP-encrypted. Recorded before decryption, so `true` can still fail. */ was_pgp_encrypted: _zod.z.boolean().optional()
});
const IntegrationLogPublicApiProperties = _zod.z.object({
    api_token_name: _zod.z.string(),
    api_id: _zod.z.string(),
    // partner_company_id and target_company_id are set only for mutations via a partner API key
    partner_company_id: _zod.z.instanceof(_bson.ObjectId).optional(),
    target_company_id: _zod.z.instanceof(_bson.ObjectId).optional()
});
const IntegrationLogProperties = _zod.z.object({
    /** List of objects representing the history of the job run. */ breadcrumbs: _zod.z.array(_zod.z.object({
        message: _zod.z.string(),
        timestamp: _zod.z.date()
    })).optional(),
    /** The number of users connect to PY from the integration system. */ connectedEmployeesCount: _zod.z.number().optional(),
    /** The name of the specific integration ex: bamboo-hr, adp-wfn etc. */ integrationName: _integration.IntegrationName
});
const IntegrationLog = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    /** The ObjectId of the company the log belongs to. **/ company_id: _zod.z.instanceof(_bson.ObjectId),
    /** Object used to store data related to the ConnectIntegrationWorkers job. **/ connect_worker_properties: _zod.z.object({
        numUsersAttemptedToSync: _zod.z.number().optional()
    }).optional(),
    /** Array of created user ids represented as strings. **/ created_users: _zod.z.array(_zod.z.string()).optional(),
    /** Object used to store settings related to importing data from a csv file. **/ csv_properties: IntegrationLogCsvProperties.optional(),
    /** The count of updates found by our ingest engine. **/ dataIngestFieldCount: _zod.z.number().optional(),
    /** An array of errors that occurred during the job. **/ errors: _zod.z.array(_zod.z.string()).optional(),
    /** An array of non-fatal warnings recorded during the job (e.g. guard-condition skips). **/ warnings: _zod.z.array(_zod.z.string()).optional(),
    /** Count of employees fetched from the integration. **/ fetchedEmployees: _zod.z.number().optional(),
    /** The time the job finished can be null if the job is still running. **/ finished_at: _zod.z.date().nullable(),
    /** The type of integration the log is for.**/ integration_type: _integration.IntegrationType,
    /** Properties of the integration. **/ integrationProperties: IntegrationLogProperties.optional(),
    /** The number of new users found in the integration system that we created integration users for. **/ newIntegrationWorkers: _zod.z.number().optional(),
    numUsersAttemptedToSync: _zod.z.number().optional(),
    public_api_properties: IntegrationLogPublicApiProperties.optional(),
    requires_approval_to_deactivate: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
    /** Role names this run auto-created, and archived names that blocked a create (one per
	 * distinct name). Absent when the run had neither. On a testing-mode log (see
	 * sftp_properties.testing_mode) `created_names` holds the names the real run WILL create —
	 * nothing was written; the UI renders them in the future tense. */ role_auto_create: _zod.z.object({
        created_names: _zod.z.array(_zod.z.string()),
        archived_name_collisions: _zod.z.array(_zod.z.string())
    }).optional(),
    sftp_properties: IntegrationLogSftpProperties.optional(),
    start_at: _zod.z.date(),
    updatedIntegrationWorkers: _zod.z.number().optional(),
    user_updates: _zod.z.array(IntegrationLogUserUpdates),
    users_created: _zod.z.number().optional(),
    users_deactivated: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
    users_failed_sync: _zod.z.number(),
    /**
	 * Count of employees where some fields applied and others failed validation. Optional: absent on
	 * logs written before PY-12481, and on Public-API logs, whose errors are whole-row write failures
	 * rather than per-field ones.
	 */ users_partially_synced: _zod.z.number().optional(),
    users_successfully_synced: _zod.z.number(),
    users_with_no_changes: _zod.z.number(),
    is_processing: _zod.z.boolean().optional(),
    deactivation_updates: _zod.z.array(_zod.z.object({
        rawValue: _zod.z.string(),
        fieldID: _zod.z.instanceof(_bson.ObjectId),
        userID: _zod.z.instanceof(_bson.ObjectId),
        updaterID: _zod.z.string(),
        comment: _zod.z.string()
    })).optional(),
    /**
	 * Array of emails that have multiple employee records.
	 * Some HRIS systems allow for multiple employment records per employee.
	 * This field is used to track employees that have multiple employment records without manual selection.
	 **/ duplicate_employee_emails: _zod.z.array(_zod.z.string()).nullish(),
    /** Groups auto-created during a CSV/SFTP or HRIS sync because the incoming name did not match. */ groups_created: _zod.z.object({
        count: _zod.z.number(),
        names: _zod.z.array(_zod.z.string())
    }).optional()
});
